import Multiselect from 'multiselect-react-dropdown';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { withTranslation } from 'react-i18next';
import { BiFilter, BiGitMerge, BiHide, BiReset, BiShow } from 'react-icons/bi';
import { FiDownload } from 'react-icons/fi';
import { LS_COMPANY_INFO_KEY, startYear } from '../../../core/constants/common';
import CompanyService from '../../../core/service/CompanyService';
import Utils from '../../../core/utilities/Utils';
import './ChartMaximizeModal.scss';
import DataSourceTreeModal from './DataSourceTreeModal';
import MaximizedChartCard from './MaximizedChartCard';

const MODAL_PROPS = {
  maxWidth: 'md',
  fullWidth: true,
  PaperProps: {
    style: {
      borderRadius: '24px',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 60%, rgba(59,130,246,0.10) 100%)',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
      backdropFilter: 'blur(18px) saturate(180%)',
      WebkitBackdropFilter: 'blur(18px) saturate(180%)',
      border: '1.5px solid rgba(255,255,255,0.25)',
      zIndex: 4000
    }
  },
  sx: {
    zIndex: 4000,
    '& .MuiBackdrop-root': {
      zIndex: 3999
    }
  }
};

// Get periodFrom/periodTo for a year based on company data frequency (FY vs calendar), same logic as HomePage
const getPeriodRangeForYear = (year, isFinancialYear) => {
  const y = Number(year) || new Date().getFullYear();
  if (isFinancialYear) {
    return {
      periodFrom: new Date(y - 1, 3, 1),   // April 1 (y-1)
      periodTo: new Date(y, 2, 31)         // March 31 (y)
    };
  }
  return {
    periodFrom: new Date(y, 0, 1),         // January 1 (y)
    periodTo: new Date(y, 11, 31)          // December 31 (y)
  };
};

const ChartMaximizeModal = ({ t, open, onClose, children, chartConfig, chartYear, chartTitle, standardId, onOpen, showLabels = false, onShowLabelsChange }) => {
  const [dataSourceTreeOpen, setDataSourceTreeOpen] = useState(false);

  // Same as HomePage: use company frequency type for period range (FY = April–March, else Jan–Dec)
  const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  const isFinancialYear = dataFreqType === 'FY';

  const initialPeriod = getPeriodRangeForYear(chartYear, isFinancialYear);

  // Filter state - periodType defaults to 'year' so one option is selected on open
  const [filters, setFilters] = useState({
    periodType: 'year',
    periodFrom: initialPeriod.periodFrom,
    periodTo: initialPeriod.periodTo,
    parentEntities: [],
    childEntities: [],
    region: '',
    compare: ''
  });

  // Entity data
  const [parentEntities, setParentEntities] = useState([]);
  const [childEntities, setChildEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Chart data IDs
  const [sankeyTree, setSankeyTree] = useState([]);

  // Refs for multiselect components
  const parentEntityRef = useRef();
  const childEntityRef = useRef();
  const maximizedChartRef = useRef();

  // Calculate year range for date picker dropdown
  const currentYearValue = new Date().getFullYear();
  const yearRange = currentYearValue - startYear + 1; // Number of years to show in dropdown


  // Load entity data on component mount
  useEffect(() => {
    if (open) {
      loadEntityData();
      // Reset period filter to default when modal opens (same FY/calendar logic as HomePage)
      if (chartYear) {
        const { periodFrom, periodTo } = getPeriodRangeForYear(chartYear, isFinancialYear);
        setFilters(prev => ({
          ...prev,
          periodFrom,
          periodTo
        }));
      }
      // Call onOpen callback if provided
      if (onOpen) {
        onOpen();
      }
    }
  }, [open, onOpen, chartConfig, chartYear, isFinancialYear]);

  // Update filters when chartYear changes (same FY/calendar logic as HomePage)
  useEffect(() => {
    if (chartYear) {
      const { periodFrom, periodTo } = getPeriodRangeForYear(chartYear, isFinancialYear);
      setFilters(prev => ({
        ...prev,
        periodFrom,
        periodTo
      }));
    }
  }, [chartYear, isFinancialYear]);

  const loadEntityData = async () => {
    setLoading(true);
    try {
      // Load parent entities (level 1)
      const parentResponse = await CompanyService.getEnterprisesByLevel(1);
      if (parentResponse.status === 200) {
        const sortedParentEntities = parentResponse.data.sort((a, b) => a.sortOrder - b.sortOrder);
        setParentEntities(sortedParentEntities);
      }

      // Load child entities (level 2)
      const childResponse = await CompanyService.getEnterprisesByLevel(2);
      if (childResponse.status === 200) {
        const sortedChildEntities = childResponse.data.sort((a, b) => a.sortOrder - b.sortOrder);
        setChildEntities(sortedChildEntities);
      }
    } catch (error) {
      console.error('Error loading entity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLabels = () => {
    onShowLabelsChange?.(!showLabels);
  };

  const handleOpenDataSourceTree = () => {
    setDataSourceTreeOpen(true);
  };

  const handleCloseDataSourceTree = () => {
    setDataSourceTreeOpen(false);
  };

  const handleBackFromDataSourceTree = () => {
    setDataSourceTreeOpen(false);
  };

  const handleDownload = () => {
    if (maximizedChartRef.current) {
      maximizedChartRef.current.downloadChart();
    }
  };

  // Filter handlers
  const handlePeriodFromChange = (date) => {
    setFilters(prev => ({
      ...prev,
      periodFrom: date
    }));
  };

  const handlePeriodToChange = (date) => {
    setFilters(prev => ({
      ...prev,
      periodTo: date
    }));
  };

  const handleParentEntitySelect = (selectedList, selectedItem) => {
    setFilters(prev => ({
      ...prev,
      parentEntities: selectedList
    }));
  };

  const handleParentEntityRemove = (selectedList, removedItem) => {
    setFilters(prev => ({
      ...prev,
      parentEntities: selectedList
    }));
  };

  const handleChildEntitySelect = (selectedList, selectedItem) => {
    setFilters(prev => ({
      ...prev,
      childEntities: selectedList
    }));
  };

  const handleChildEntityRemove = (selectedList, removedItem) => {
    setFilters(prev => ({
      ...prev,
      childEntities: selectedList
    }));
  };

  const handleRegionChange = (e) => {
    setFilters(prev => ({
      ...prev,
      region: e.target.value
    }));
  };

  const handlePeriodToMonthOrYearChange = (data, type, periodFromOrTo) => {
    if (data && type === 'month') {
      setFilters(prev => ({
        ...prev,
        [periodFromOrTo]: data
      }));
    } else if (type === 'year') {
      setFilters(prev => ({
        ...prev,
        [periodFromOrTo]: data
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [periodFromOrTo]: null
      }));
    }
  };


  const handleCompareChange = (e) => {
    setFilters(prev => ({
      ...prev,
      compare: e.target.value
    }));
  };

  const handleApplyFilters = () => {
    maximizedChartRef.current?.fetchChartData();
  };

  const handleResetFilters = () => {
    const { periodFrom, periodTo } = getPeriodRangeForYear(chartYear, isFinancialYear);
    const resetFilters = {
      periodType: 'year',
      periodFrom,
      periodTo,
      parentEntities: [],
      childEntities: [],
      region: '',
      compare: ''
    };
    setFilters(resetFilters);

    // Clear multiselect selections
    if (parentEntityRef.current) {
      parentEntityRef.current.resetSelectedValues();
    }
    if (childEntityRef.current) {
      childEntityRef.current.resetSelectedValues();
    }

    maximizedChartRef.current?.fetchChartData(resetFilters);
  };

  // Handle modal close with full filter reset (same FY/calendar logic as HomePage)
  const handleClose = () => {
    const y = chartYear || currentYearValue;
    const { periodFrom, periodTo } = getPeriodRangeForYear(y, isFinancialYear);
    setFilters({
      periodType: 'year',
      periodFrom,
      periodTo,
      parentEntities: [],
      childEntities: [],
      region: '',
      compare: ''
    });

    // Show filters panel again on next open
    setShowFilters(true);

    // Clear multiselect selections so next open starts clean
    if (parentEntityRef.current) {
      parentEntityRef.current.resetSelectedValues();
    }
    if (childEntityRef.current) {
      childEntityRef.current.resetSelectedValues();
    }

    onClose();
  };

  // Only show one modal at a time
  if (dataSourceTreeOpen) {
    // Combine parent and child entity IDs
    const parentEntityIds = filters.parentEntities.map(entity => entity.id);
    const childEntityIds = filters.childEntities.map(entity => entity.id);
    const allBusinessUnitIds = [...parentEntityIds, ...childEntityIds].join(',');

    return (
      <DataSourceTreeModal
        open={true}
        onClose={handleCloseDataSourceTree}
        onBack={handleBackFromDataSourceTree}
        graphId={chartConfig?.id}
        graphName={chartConfig?.title || chartTitle}
        standardId={standardId}
        sankeyTree={sankeyTree}
        periodFrom={filters.periodFrom ? filters.periodFrom.toISOString().split('T')[0] : null}
        periodTo={filters.periodTo ? filters.periodTo.toISOString().split('T')[0] : null}
        businessUnitIds={allBusinessUnitIds}
        {...MODAL_PROPS}
      />
    );
  }

  return (
    <Modal show={open} onHide={handleClose} centered className="NewDashboard_modal" dialogClassName="modal-95w">
      <Modal.Body>
        <div className="d-flex">
          {showFilters && (
            <div className="chart-maximize-modal-filters">
              <div className='d-flex align-items-center justify-content-between'>
                <h4 className='mb-0'>{t('ChartMaximizeModal.filters.label')}</h4>
                <button
                  type="button"
                  className="btn btn-sm btn-close border-0 ms-2"
                  aria-label="Close"
                  onClick={() => setShowFilters(false)}
                />
              </div>

                <div className="filter-group">
                  <label>{t('ChartMaximizeModal.period.label')}</label>
                  <div className="date-picker-container">
                    <ReactDatePicker
                      selected={filters.periodFrom}
                      onChange={handlePeriodFromChange}
                      selectsStart
                      startDate={filters.periodFrom}
                      endDate={filters.periodTo}
                      maxDate={filters.periodTo || new Date(currentYearValue, 11, 31)}
                      minDate={new Date(startYear, 0, 1)}
                      className="date-picker-input"
                      dateFormat="dd/MM/yyyy"
                      placeholderText={t('ChartMaximizeModal.fromDate.label')}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      onMonthChange={(month) => handlePeriodToMonthOrYearChange(month, 'month', 'periodFrom')}
                      onYearChange={(year) => handlePeriodToMonthOrYearChange(year, 'year', 'periodFrom')}
                      dropdownMode="select"
                      yearDropdownItemNumber={yearRange}
                      scrollableYearDropdown
                    />
                    <ReactDatePicker
                      selected={filters.periodTo}
                      onChange={handlePeriodToChange}
                      selectsEnd
                      startDate={filters.periodFrom}
                      endDate={filters.periodTo}
                      minDate={filters.periodFrom || new Date(startYear, 0, 1)}
                      maxDate={new Date(currentYearValue, 11, 31)}
                      className="date-picker-input"
                      dateFormat="dd/MM/yyyy"
                      placeholderText={t('ChartMaximizeModal.toDate.label')}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      onMonthChange={(month) => handlePeriodToMonthOrYearChange(month, 'month', 'periodTo')}
                      onYearChange={(year) => handlePeriodToMonthOrYearChange(year, 'year', 'periodTo')}
                      dropdownMode="select"
                      yearDropdownItemNumber={yearRange}
                      scrollableYearDropdown
                    />
                  </div>
                </div>
              {/* </>)} */}

              {/* Parent Entity Filter */}
              <div className="filter-group">
                <label>{t('ChartMaximizeModal.parentEntity.label')}</label>
                <Multiselect
                  options={parentEntities}
                  selectedValues={filters.parentEntities}
                  onSelect={handleParentEntitySelect}
                  onRemove={handleParentEntityRemove}
                  displayValue="name"
                  placeholder={t('ChartMaximizeModal.selectParentEntity.label')}
                  ref={parentEntityRef}
                  className="multiselect-dropdown"
                  avoidHighlightFirstOption={true}
                  style={{
                    chips: { maxWidth: '200px', whiteSpace: 'normal' },
                    searchBox: { border: '1px solid #e5e7eb', borderRadius: '8px' }
                  }}
                />
              </div>

              {/* Child Entity Filter */}
              <div className="filter-group">
                <label>{t('ChartMaximizeModal.childEntity.label')}</label>
                <Multiselect
                  options={childEntities}
                  selectedValues={filters.childEntities}
                  onSelect={handleChildEntitySelect}
                  onRemove={handleChildEntityRemove}
                  displayValue="name"
                  placeholder={t('ChartMaximizeModal.selectChildEntity.label')}
                  ref={childEntityRef}
                  className="multiselect-dropdown"
                  avoidHighlightFirstOption={true}
                  style={{
                    chips: { maxWidth: '200px', whiteSpace: 'normal' },
                    searchBox: { border: '1px solid #e5e7eb', borderRadius: '8px' }
                  }}
                />
              </div>

              {/* Region Filter - Hidden for now */}
              {/* <div className="filter-group">
            <label>{t('ChartMaximizeModal.region.label')}</label>
            <select 
              className="filter-select"
              value={filters.region}
              onChange={handleRegionChange}
            >
              <option value="">{t('ChartMaximizeModal.selectRegion.label')}</option>
              <option value="global">{t('ChartMaximizeModal.global.label')}</option>
              <option value="europe">{t('ChartMaximizeModal.europe.label')}</option>
              <option value="asia">{t('ChartMaximizeModal.asia.label')}</option>
              <option value="americas">{t('ChartMaximizeModal.americas.label')}</option>
            </select>
          </div> */}

              {/* Compare Filter - Hidden for now */}
              {/* <div className="filter-group">
            <label>{t('ChartMaximizeModal.compare.label')}</label>
            <select 
              className="filter-select"
              value={filters.compare}
              onChange={handleCompareChange}
            >
              <option value="">{t('ChartMaximizeModal.selectCompare.label')}</option>
              <option value="previous-year">{t('ChartMaximizeModal.previousYear.label')}</option>
              <option value="previous-period">{t('ChartMaximizeModal.previousPeriod.label')}</option>
              <option value="target">{t('ChartMaximizeModal.target.label')}</option>
            </select>
          </div> */}

              <div className="filter-actions">
                <Button
                  variant="primary"
                  onClick={handleApplyFilters}
                  className="apply-btn w-100"
                >
                  {t('ChartMaximizeModal.apply.label')}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleResetFilters}
                  title={t('ChartMaximizeModal.resetTooltip.label')}
                  className="reset-btn"
                >
                  <BiReset />
                </Button>
              </div>
            </div>
          )}

          <div className="chart-maximize-modal-chart">
            <div className='chart-maximize-modal-chart-header'>
              <h6 className='mb-0'>{chartTitle || t('ChartMaximizeModal.title.label')}</h6>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: 'auto' }}>
                <OverlayTrigger
                  placement="top"
                  overlay={Utils.renderTooltip(
                    showLabels ? t('hideLabels.label') : t('viewDataLabels.label')
                  )}
                >
                  <span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleToggleLabels}
                      className="border-0"
                      aria-label={showLabels ? t('hideLabels.label') : t('viewDataLabels.label')}
                    >
                      {showLabels ? <BiHide /> : <BiShow />}
                    </Button>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={Utils.renderTooltip(
                    `${t('Download.label')}`
                  )}><span><FiDownload
                    className="chart-download-icon"
                    onClick={handleDownload}
                    style={{ cursor: 'pointer', marginRight: '8px' }}
                  /></span>
                </OverlayTrigger>
                {<OverlayTrigger
                  trigger="hover"
                  placement="bottom"
                  overlay={<Tooltip id="tooltip-bscopy">{t("ChartMaximizeModal.sankeyDiagram.label")}</Tooltip>}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleOpenDataSourceTree}
                    className="border-0"
                  >
                    <BiGitMerge />
                  </Button>
                </OverlayTrigger>}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowFilters(prev => !prev)}
                  className="border-0"
                  title={t('ChartMaximizeModal.filters.label')}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <BiFilter />
                </Button>

                <button type="button" className="btn btn-sm btn-close border-0 ms-2" aria-label="Close" onClick={handleClose}></button>
              </div>

            </div>

            {chartConfig ? (
              <MaximizedChartCard
                ref={maximizedChartRef}
                config={chartConfig}
                year={
                  filters.periodFrom
                    ? filters.periodFrom.getFullYear()
                    : chartYear
                }
                title={chartTitle}
                type={chartConfig.type || 'BAR'}
                height={400} // Increased height for better popup utilization
                showLegend={true}
                filters={filters}
                standardId={standardId}
                showLabels={showLabels}
                onShowLabelsChange={onShowLabelsChange}
                onDataUpdate={(data) => {
                  setSankeyTree(data.sankeyTree);
                }}
              />
            ) : (
              children
            )}
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
};

ChartMaximizeModal.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  chartConfig: PropTypes.object,
  chartYear: PropTypes.number,
  chartTitle: PropTypes.string,
  standardId: PropTypes.number,
  showLabels: PropTypes.bool,
  onShowLabelsChange: PropTypes.func
};

export default withTranslation()(ChartMaximizeModal); 